import { useMemo, useState, useCallback } from 'react';
import { Box, DialogActions, DialogTitle, DialogContent } from '@mui/material';

import { Button, SearchForm, TableContainer, Typography } from '@occc-app/design-system';
import { KioskFilter, KioskFragment } from '@occc/graphql';
import { PaginationInfo } from '@occc-app/hooks/usePaginatedQuery';

import { TouchpointKiosksTable } from './touchpoint-kiosks-table.component';

import { useGetKiosks } from '../../hooks/use-get-kiosks.hook';
import {
  AD_TERMINAL_KIOSK_IDS,
  ADD_TARGET_BUTTON_TEXT,
  CANCEL_BUTTON_TEXT,
} from '../../touchpoint.constants';

type KiosksTableContainerProps = {
  onClose: () => void;
  onConfirm: (selectedIds: string[]) => void;
  initialKioskIds?: string[];
  maxSelection?: number;
  hideBulkSelect?: boolean;
};

export const TouchpointKiosksTableContainer = ({
  onClose,
  onConfirm,
  initialKioskIds = [],
  maxSelection,
  hideBulkSelect = false,
}: KiosksTableContainerProps) => {
  const [search, setSearch] = useState<string | undefined>(undefined);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set(initialKioskIds));
  const [paginationInfo, setPaginationInfo] = useState<PaginationInfo | undefined>();

  const kioskFilter = useMemo(() => ({ searchTerm: search || undefined } as KioskFilter), [search]);

  const { loading, error, data, paginationProps } = useGetKiosks({
    filter: kioskFilter,
    paginationInfo,
    onPageChange: setPaginationInfo,
  });

  const handleKioskSelect = useCallback(
    (kioskId: string, _kiosk?: KioskFragment) => {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(kioskId)) {
          next.delete(kioskId);
        } else {
          if (maxSelection && next.size >= maxSelection) {
            return prev;
          }
          next.add(kioskId);
        }
        return next;
      });
    },
    [maxSelection],
  );

  const handleBulkSelectMultiple = useCallback(
    (currentPageItems: string[], currentPageSelectedItems: string[]) => {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        const isDeselecting = currentPageSelectedItems.length > 0;

        if (isDeselecting) {
          currentPageItems.forEach((id) => next.delete(id));
        } else {
          if (maxSelection) {
            const availableSlots = maxSelection - next.size;
            const itemsToAdd = currentPageItems.slice(0, availableSlots);
            itemsToAdd.forEach((id) => next.add(id));
          } else {
            currentPageItems.forEach((id) => next.add(id));
          }
        }
        return next;
      });
    },
    [maxSelection],
  );

  const handleBulkDeselectAll = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const onSubmit = useCallback(() => {
    onConfirm(Array.from(selectedIds));
    onClose();
  }, [selectedIds, onConfirm, onClose]);

  const handleSearchSubmit = useCallback(({ search }: { search?: string }) => {
    setSearch(search || undefined);
    setPaginationInfo(undefined);
  }, []);

  const headerActions = useMemo(
    () => <SearchForm onSubmit={handleSearchSubmit} />,
    [handleSearchSubmit],
  );

  const addTargetButtonText = `${ADD_TARGET_BUTTON_TEXT}${
    selectedIds.size > 0 ? ` (${selectedIds.size})` : ''
  }`;

  return (
    <>
      <DialogTitle>
        <Box display='flex' alignItems='center' justifyContent='space-between'>
          <Typography variant='h6' whiteSpace='nowrap'>
            {AD_TERMINAL_KIOSK_IDS}
          </Typography>
          {headerActions}
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        <TableContainer>
          <TouchpointKiosksTable
            loading={loading}
            data={data}
            error={error}
            paginationProps={paginationProps}
            bulkSelectedItems={selectedIds}
            handleBulkSelectMultiple={hideBulkSelect ? undefined : handleBulkSelectMultiple}
            handleItemSelect={handleKioskSelect}
            handleBulkDeselectAll={handleBulkDeselectAll}
            hideBulkSelect={hideBulkSelect}
            maxSelection={maxSelection}
          />
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button variant='text' onClick={onClose}>
          {CANCEL_BUTTON_TEXT}
        </Button>
        <Button variant='contained' color='primary' onClick={onSubmit}>
          {addTargetButtonText}
        </Button>
      </DialogActions>
    </>
  );
};
